// Postavenie voči obalu sa modeluje ako DVE NEZÁVISLÉ polia (baseRole + producerRole), nie jeden
// plochý enum - vyplýva to z čl. 3 ods. 1 bod 15 PPWR: "výrobcom je každý ZHOTOVITEĽ, DOVOZCA
// alebo DISTRIBÚTOR", teda EPR postavenie výrobcu sa KOMBINUJE so základnou rolou, nenahrádza ju
// (napr. jednotka môže byť súčasne Distribútor aj Výrobca). Kód hodnôt musí zostať v súlade
// s server/models/packagingUnit.js a server/config/packagingLabels*.js.

// MANUFACTURER_CUSTOMER = subjekt fyzicky vyrába/plní obal, ale zhotoviteľom (čl. 3/13) je jeho
// zákazník (obal nesie jeho značku alebo on rozhoduje o dizajne) - subjekt vystupuje len ako dodávateľ.
// MANUFACTURER_SUPPLIER = mikropodniková výnimka - zhotoviteľom je dodávateľ obalu.
// Pri oboch subjekt nie je zhotoviteľ/dovozca/distribútor, preto sa u nich rola výrobcu (čl. 3/15)
// vôbec netestuje - producerRole je vždy NOT_APPLICABLE.
// NOT_MANUFACTURER je zachované len pre spätnú kompatibilitu so staršími záznamami.
export const BASE_ROLE_LABELS = {
    MANUFACTURER_SELF: 'Zhotoviteľ',
    MANUFACTURER_SUPPLIER: 'Dodávateľ (mikropodniková výnimka)',
    MANUFACTURER_CUSTOMER: 'Dodávateľ',
    NOT_MANUFACTURER: 'Dovozca / distribútor',
    IMPORTER: 'Dovozca',
    DISTRIBUTOR: 'Distribútor',
};

export const BASE_ROLE_VARIANTS = {
    MANUFACTURER_SELF: 'success',
    MANUFACTURER_SUPPLIER: 'primary',
    MANUFACTURER_CUSTOMER: 'primary',
    NOT_MANUFACTURER: 'warning',
    IMPORTER: 'warning',
    DISTRIBUTOR: 'info',
};

// producerRole - nezávislé postavenie výrobcu podľa čl. 3 ods. 1 bod 15 PPWR (EPR) - kombinuje
// sa s baseRole. NOT_APPLICABLE = subjekt nepatrí medzi zhotoviteľa/dovozcu/distribútora, test
// sa preň vôbec nespúšťa (nezobrazuje sa ako samostatný badge).
export const PRODUCER_ROLE_LABELS = {
    PRODUCER: 'Výrobca',
    PRODUCER_OTHER: 'výrobca je iný subjekt',
    PRODUCER_EXEMPT_EXPORT: 'vývoz mimo EÚ, bez EPR',
    NOT_PRODUCER: 'bez EPR povinností v tomto ČŠ',
    NOT_APPLICABLE: '',
};

export const PRODUCER_ROLE_VARIANTS = {
    PRODUCER: 'dark',
    PRODUCER_OTHER: 'secondary',
    PRODUCER_EXEMPT_EXPORT: 'secondary',
    NOT_PRODUCER: 'secondary',
};

// NOT_MANUFACTURER sa v selecte nikdy neponúka - je to legacy hodnota len na zobrazenie starších
// záznamov (viď komentár pri BASE_ROLE_LABELS), nová diagnostika/úprava ju už nemá priraďovať.
export const BASE_ROLE_OPTIONS = Object.entries(BASE_ROLE_LABELS)
    .filter(([value]) => value !== 'NOT_MANUFACTURER')
    .map(([value, label]) => ({ value, label }));
export const PRODUCER_ROLE_OPTIONS = Object.entries(PRODUCER_ROLE_LABELS)
    .filter(([value]) => value !== 'NOT_APPLICABLE')
    .map(([value, label]) => ({ value, label: label.charAt(0).toUpperCase() + label.slice(1) }));

// Kombinuje baseRole + producerRole do jedného zobrazovaného textu (napr. "Distribútor + Výrobca"),
// napr. pre PDF alebo miesta, kde nie je priestor na dva samostatné badge (viď getRoleBadges nižšie).
export const getCombinedRoleLabel = (baseRole, producerRole) => {
    const baseLabel = BASE_ROLE_LABELS[baseRole] ?? baseRole;
    if (!producerRole || producerRole === 'NOT_APPLICABLE') return baseLabel;
    if (producerRole === 'PRODUCER') return `${baseLabel} + ${PRODUCER_ROLE_LABELS.PRODUCER}`;
    return `${baseLabel} (${PRODUCER_ROLE_LABELS[producerRole] ?? producerRole})`;
};

// UI zobrazuje baseRole a producerRole ako DVA samostatné badge (nie jeden zlúčený text) - lepšie
// vysvetľuje používateľovi, že ide o dve nezávislé zodpovednosti (viď implementačná poznámka #6).
// Druhý badge sa zobrazí len keď je jednotka SKUTOČNE výrobcom (producerRole === 'PRODUCER') -
// ostatné hodnoty (PRODUCER_OTHER, PRODUCER_EXEMPT_EXPORT, NOT_PRODUCER) len vysvetľujú, PREČO
// výrobcom nie je, čo do kompaktného badgeu v tabuľke/zozname nepatrí (viď detail jednotky/wizard).
export const getRoleBadges = (baseRole, producerRole) => {
    const badges = [{ label: BASE_ROLE_LABELS[baseRole] ?? baseRole, variant: BASE_ROLE_VARIANTS[baseRole] ?? 'secondary' }];
    if (producerRole === 'PRODUCER') {
        badges.push({ label: PRODUCER_ROLE_LABELS.PRODUCER, variant: PRODUCER_ROLE_VARIANTS.PRODUCER });
    }
    return badges;
};

// Filter v zozname obalových jednotiek - bázové roly filtrujú podľa baseRole, "Výrobca" naviac
// filtruje nezávisle podľa producerRole (jednotka môže byť napr. Distribútor AJ Výrobca súčasne).
export const ROLE_FILTER_OPTIONS = [
    { value: 'MANUFACTURER_SELF', label: 'Zhotoviteľ' },
    { value: 'MANUFACTURER_SUPPLIER', label: 'Dodávateľ (mikropodniková výnimka)' },
    { value: 'MANUFACTURER_CUSTOMER', label: 'Dodávateľ' },
    { value: 'IMPORTER', label: 'Dovozca' },
    { value: 'DISTRIBUTOR', label: 'Distribútor' },
    { value: 'PRODUCER', label: 'Výrobca (čl. 3/15)' },
];

const BASE_ROLE_FILTER_GROUPS = {
    MANUFACTURER_SELF: ['MANUFACTURER_SELF'],
    MANUFACTURER_SUPPLIER: ['MANUFACTURER_SUPPLIER'],
    MANUFACTURER_CUSTOMER: ['MANUFACTURER_CUSTOMER'],
    IMPORTER: ['IMPORTER', 'NOT_MANUFACTURER'],
    DISTRIBUTOR: ['DISTRIBUTOR'],
};

export const matchesRoleFilter = (unit, filterValue) => {
    if (!filterValue) return true;
    if (filterValue === 'PRODUCER') return unit.producerRole === 'PRODUCER';
    return BASE_ROLE_FILTER_GROUPS[filterValue]?.includes(unit.baseRole) ?? false;
};

// Jednotka potrebuje plnú dokumentáciu zhotoviteľa (posúdenie zhody, EÚ vyhlásenie, technická
// dokumentácia, čl. 15 PPWR) buď keď JE zhotoviteľom (baseRole), alebo keď JE výrobcom podľa
// čl. 3/15 (producerRole) - obe zodpovednosti sa kombinujú nezávisle.
export const requiresManufacturerDocs = (baseRole, producerRole) =>
    baseRole === 'MANUFACTURER_SELF' || producerRole === 'PRODUCER';

export const DOCUMENT_CATEGORY_KEYS = ['SUPPLIER_DOCS', 'CONFORMITY_ASSESSMENT', 'EU_DECLARATION', 'TECHNICAL_DOCS'];

// Skladá zoznam požadovaných kategórií dokumentov nezávisle podľa oboch polí - DISTRIBUTOR_VERIFICATION
// (čl. 19) sa pridáva podľa baseRole, PRODUCER_VERIFICATION (čl. 44) nezávisle podľa producerRole,
// takže sa môžu kombinovať (napr. Distribútor + Výrobca rieši oboje, navyše plnú dokumentáciu zhotoviteľa).
export const getRequiredDocumentCategoryKeys = (baseRole, producerRole) => {
    const keys = requiresManufacturerDocs(baseRole, producerRole) ? [...DOCUMENT_CATEGORY_KEYS] : ['SUPPLIER_DOCS'];
    if (baseRole === 'DISTRIBUTOR') keys.push('DISTRIBUTOR_VERIFICATION');
    if (producerRole === 'PRODUCER') keys.push('PRODUCER_VERIFICATION');
    return keys;
};
